// Cómo se lee un hato: en qué categoría está cada animal y cómo viene creciendo.
//
// Ninguna de estas dos cosas se guarda en la base. Las dos salen de datos que ya
// están ahí —el sexo, la fecha de nacimiento, los partos anotados, los pesajes—
// y por eso viven aquí y no en una columna: una categoría guardada envejece sola
// y hay que acordarse de actualizarla. Esta se calcula cada vez que se mira.

const DIA = 86400000;

// Todas las funciones de aquí reciben lo que devuelve el servidor. Si una
// petición falla, eso puede no ser una lista sino un objeto de error — y
// `objeto || []` lo deja pasar tal cual, que es cómo una pantalla entera se
// cae por un error de red. Se comprueba que sea lista, no que exista.
const lista = (x) => (Array.isArray(x) ? x : []);

// Un mes ganadero es de 30 días y medio, no del calendario. Nadie en una finca
// dice «tiene 11 meses y 27 días»: dice «va para el año».
const MES = 30.4375;

// Los cortes de edad, en meses. Van aquí arriba y sueltos porque son
// convención, no ley: cada región y cada finca los mueve un poco, y el día que
// haya que ajustarlos tiene que ser una línea y no una búsqueda.
export const CORTES = {
  // Hasta el año es cría, sin importar el sexo.
  cria: 12,
  // De ahí en adelante el macho se llama novillo; pasado el segundo año, y si
  // sigue entero, se le dice toro. Como el programa no sabe si está castrado,
  // esto es por edad — el que ceba novillos puede subir este número.
  toro: 24,
};

const aFecha = (f) => {
  if (!f) return null;
  const d = f instanceof Date ? f : new Date(String(f).length <= 10 ? `${f}T00:00:00` : f);
  return isNaN(d.getTime()) ? null : d;
};

export function mesesDeEdad(animal, hoy = new Date()) {
  const nacio = aFecha(animal && animal.fecha_nacimiento);
  if (!nacio) return null;
  const dias = (aFecha(hoy) - nacio) / DIA;
  return dias < 0 ? null : dias / MES;
}

// Cuántos partos lleva cada animal, sacados de los eventos que la pantalla ya
// tiene cargados. No hace falta pedirle nada nuevo al servidor: la cuenta está
// en los mismos datos con los que se dibuja la ficha.
export function partosPorAnimal(eventos) {
  const cuenta = new Map();
  for (const e of lista(eventos)) {
    if (e && e.tipo === 'parto') {
      cuenta.set(e.ganado_id, (cuenta.get(e.ganado_id) || 0) + 1);
    }
  }
  return cuenta;
}

// Los pesajes de un animal, que es lo único que mira `gananciaDiaria`.
export function pesajesDe(eventos, ganadoId) {
  return lista(eventos).filter(e => e && e.tipo === 'pesaje' && e.ganado_id === ganadoId);
}

// La categoría, con las palabras de la finca.
//
// La hembra que ya parió es vaca aunque tenga dos años; la que no, es novilla
// aunque tenga cinco. Eso lo decide el parto, no la edad, y por eso hace falta
// saber cuántos partos lleva: viene en `partos`, que el servidor cuenta de los
// eventos.
//
// Lo que TODAVÍA no se puede decir es si una vaca está horra o parida — eso
// necesita el ciclo reproductivo, que es la fase siguiente. Mientras tanto se
// dice «vaca» y ya, en vez de adivinar.
export function categoriaDelAnimal(animal, hoy = new Date()) {
  if (!animal) return { clave: 'sin_datos', nombre: 'Sin datos' };

  const hembra = String(animal.sexo || '').toLowerCase().startsWith('h');
  const partos = Number(animal.partos) || 0;
  const meses = mesesDeEdad(animal, hoy);

  // Una hembra con partos es vaca aunque no se sepa cuándo nació. Es el único
  // caso que no necesita la edad, y es frecuente: del animal comprado muchas
  // veces solo se sabe que ya venía pariendo.
  if (hembra && partos > 0) return { clave: 'vaca', nombre: 'Vaca', meses, partos };

  if (meses === null) {
    return { clave: 'sin_edad', nombre: 'Sin fecha de nacimiento', meses: null, partos };
  }

  if (meses < CORTES.cria) {
    return hembra
      ? { clave: 'ternera', nombre: 'Ternera', meses, partos }
      : { clave: 'ternero', nombre: 'Ternero', meses, partos };
  }

  if (hembra) return { clave: 'novilla', nombre: 'Novilla', meses, partos };

  return meses < CORTES.toro
    ? { clave: 'novillo', nombre: 'Novillo', meses, partos }
    : { clave: 'toro', nombre: 'Toro', meses, partos };
}

// Cuántos hay de cada categoría, en el orden en que se lee un hato: primero lo
// que produce, después lo que viene subiendo.
export const ORDEN_CATEGORIAS = ['vaca', 'novilla', 'ternera', 'toro', 'novillo',
                                 'ternero', 'sin_edad', 'sin_datos'];

export function estructuraDelHato(animales, hoy = new Date()) {
  const cuenta = new Map();
  for (const a of lista(animales)) {
    // El vendido y el muerto no son parte del hato de hoy. Contarlos hace que
    // la estructura no cuadre con lo que hay en el potrero, que es contra lo
    // que la gente la compara.
    if (a.estado && a.estado !== 'activo') continue;
    const { clave, nombre } = categoriaDelAnimal(a, hoy);
    const antes = cuenta.get(clave) || { clave, nombre, cuantos: 0 };
    antes.cuantos += 1;
    cuenta.set(clave, antes);
  }
  return ORDEN_CATEGORIAS
    .filter(c => cuenta.has(c))
    .map(c => cuenta.get(c));
}

// Qué falta hacerle a cada animal.
//
// Anotar una vacuna sin decir cuándo toca la próxima es escribir en un cuaderno:
// queda constancia de lo hecho y nada avisa de lo que falta. En una finca lo que
// se olvida no es lo que ya se hizo.
//
// LA REGLA QUE HACE QUE ESTO SIRVA: de cada animal y cada tipo de trabajo solo
// cuenta la anotación MÁS RECIENTE. Si a la vaca 101 se le puso la vacuna en
// enero con próxima en julio, y en julio se le vuelve a poner, la tarea de enero
// desaparece sola — porque la de julio la reemplazó.
//
// Sin eso la lista solo crece: cada vacuna puesta deja su pendiente para
// siempre, a los tres meses hay doscientos renglones y nadie la vuelve a
// mirar. Una lista que no se vacía no es un recordatorio, es ruido.
export function loQueToca(eventos, animales, hoy = new Date()) {
  const vacio = { vencidas: [], pronto: [], adelante: [] };

  // Solo del ganado que está en el potrero. Una purga pendiente de un animal
  // vendido hace cuatro meses es exactamente el ruido que se quiere evitar.
  const activos = new Map();
  for (const a of lista(animales)) {
    if (!a.estado || a.estado === 'activo') activos.set(a.id, a);
  }
  if (activos.size === 0) return vacio;

  // La última anotación de cada animal y cada tipo.
  const ultima = new Map();
  for (const e of lista(eventos)) {
    if (!e || !activos.has(e.ganado_id)) continue;
    const f = aFecha(e.fecha);
    if (!f) continue;
    const llave = `${e.ganado_id}|${e.tipo}`;
    const antes = ultima.get(llave);
    if (!antes || f > antes.fecha) ultima.set(llave, { evento: e, fecha: f });
  }

  const dia = (d) => Math.floor(d.getTime() / DIA);
  const dHoy = dia(aFecha(hoy));

  const tareas = [];
  for (const { evento, fecha } of ultima.values()) {
    const proxima = aFecha(evento.proxima_fecha);
    if (!proxima) continue;
    const animal = activos.get(evento.ganado_id);
    tareas.push({
      animal,
      arete: animal.arete,
      nombre: animal.nombre,
      tipo: evento.tipo,
      ultimaVez: fecha,
      proxima,
      // En días enteros, no en instantes: comparar momentos hace que «hoy»
      // parezca ayer o mañana según la hora a la que se mire la pantalla.
      dias: dia(proxima) - dHoy,
      nota: evento.nota,
    });
  }

  tareas.sort((a, b) => a.dias - b.dias);

  return {
    vencidas: tareas.filter(t => t.dias < 0),
    pronto: tareas.filter(t => t.dias >= 0 && t.dias <= 7),
    // Un mes por delante. Más allá no es un recordatorio, es el calendario
    // entero, y estorba a lo que sí hay que hacer esta semana.
    adelante: tareas.filter(t => t.dias > 7 && t.dias <= 30),
  };
}

// La venta en pie, mirada por kilo.
//
// El total de una venta dice cuánta plata entró y nada más. La pregunta del que
// ceba es otra: «¿me pagaron bien?», y esa se contesta comparando el precio del
// kilo al que compró contra el precio del kilo al que vendió. Es la misma cuenta
// que hace de cabeza apoyado en el corral.
//
// Los kilos de salida son opcionales a propósito: hay ventas de bulto donde
// nadie pesó nada. Cuando faltan se devuelve lo que se pueda y nulo lo demás,
// nunca un cero — un «$0 el kilo» se lee como que se lo regalaron.
export function porKilo(animal) {
  if (!animal) return null;

  const kilosSalida = Number(animal.kilos_salida) || null;
  const kilosEntrada = Number(animal.peso_ingreso) || null;
  const compra = Number(animal.precio_compra) || 0;
  const venta = Number(animal.precio_venta) || null;

  const compraKilo = kilosEntrada && compra > 0
    ? Math.round(compra / kilosEntrada) : null;
  const ventaKilo = animal.precio_kilo
    ? Math.round(Number(animal.precio_kilo))
    : (kilosSalida && venta ? Math.round(venta / kilosSalida) : null);

  // Lo que engordó mientras estuvo en la finca.
  const kilosGanados = kilosSalida && kilosEntrada
    ? Math.round((kilosSalida - kilosEntrada) * 10) / 10 : null;

  if (compraKilo === null && ventaKilo === null && kilosGanados === null) return null;

  return {
    kilosEntrada,
    kilosSalida,
    kilosGanados,
    compraKilo,
    ventaKilo,
    // Cuánto subió el precio del kilo entre que lo compró y lo vendió. Puede
    // ser negativo, y cuando lo es hay que verlo: significa que el animal
    // engordó pero el mercado se le cayó encima.
    diferenciaKilo: compraKilo !== null && ventaKilo !== null
      ? ventaKilo - compraKilo : null,
  };
}

// Cómo viene creciendo un animal, en gramos por día.
//
// Se dice en gramos y no en kilos porque así se habla: «me está dando 650
// gramos» es una frase de finca; «0,65 kg/día» hay que traducirla.
//
// Se calculan dos cosas distintas y las dos importan:
//
//   toda la vida  desde que entró o nació hasta el último pesaje. Es el número
//                 que va contra el precio de compra y dice si el animal sirvió.
//   el último     entre los dos pesajes más recientes. Es el que avisa: un
//                 animal que venía bien y se frenó no se ve en el promedio de
//                 toda la vida, se ve aquí.
//
// Sin dos puntos no hay ganancia que calcular, y devolver un cero sería peor
// que devolver nada: un cero se lee como «no está creciendo».
export function gananciaDiaria(animal, pesajes, hoy = new Date()) {
  const puntos = [];

  // El punto de partida: el peso de ingreso con su fecha. Puede no estar.
  const inicio = aFecha(animal && (animal.fecha_ingreso || animal.fecha_nacimiento));
  if (inicio && Number(animal.peso_ingreso) > 0) {
    puntos.push({ fecha: inicio, kilos: Number(animal.peso_ingreso) });
  }

  for (const p of lista(pesajes)) {
    const f = aFecha(p.fecha);
    const kilos = Number(p.valor);
    if (f && kilos > 0) puntos.push({ fecha: f, kilos });
  }

  puntos.sort((a, b) => a.fecha - b.fecha);
  if (puntos.length < 2) return null;

  const gramosEntre = (a, b) => {
    const dias = (b.fecha - a.fecha) / DIA;
    if (dias <= 0) return null;
    return Math.round((b.kilos - a.kilos) * 1000 / dias);
  };

  const primero = puntos[0];
  const ultimo = puntos[puntos.length - 1];
  const penultimo = puntos[puntos.length - 2];

  return {
    // El tramo entero
    gramosPorDia: gramosEntre(primero, ultimo),
    desde: primero.fecha,
    hasta: ultimo.fecha,
    kilosGanados: Math.round((ultimo.kilos - primero.kilos) * 10) / 10,
    pesoActual: ultimo.kilos,
    // El último tramo, que es el que avisa
    gramosUltimoTramo: gramosEntre(penultimo, ultimo),
    diasSinPesar: Math.floor((aFecha(hoy) - ultimo.fecha) / DIA),
    pesajes: puntos.length,
  };
}
